import { useEffect, useMemo } from 'react'
import { Pie } from 'react-chartjs-2'
import 'chart.js/auto'
import { DialogTitle } from '@/components/ui/dialog'
import { useHall } from '@/contexts/HallProvider'
import { HallUser, SpeakingRole } from '@db8/shared'
import { usePoll } from '@/contexts/PollProvider'
import useDarkMode from '@/hooks/useDarkMode'
import TextLoading from '@/components/ui/textLoading'

type SimplePoll = {
  [role in SpeakingRole.Affirmative | SpeakingRole.Negative]: HallUser[]
}

export const PieChartResults = ({ onNext, waitUntilBallotComplete = false }) => {
  const { hall, hallMetadata, isOwner } = useHall()
  const { pollResults, hasEveryoneVoted, determineWinner } = usePoll()
  const isDark = useDarkMode()

  const simpleResults = pollResults as SimplePoll

  const affirmativeVotes = simpleResults[SpeakingRole.Affirmative]?.length || 0
  const negativeVotes = simpleResults[SpeakingRole.Negative]?.length || 0

  useEffect(() => {
    if (isOwner) determineWinner()
  }, [hall, hasEveryoneVoted])

  const data = useMemo(
    () => ({
      labels: [hall.affirmingTeamName, hall.dissentingTeamName],
      datasets: [
        {
          data: [affirmativeVotes, negativeVotes],
          backgroundColor: (context) => {
            const { ctx, chartArea } = context.chart
            if (!chartArea) {
              return context.dataIndex === 0 ? 'rgba(34, 197, 94, 1)' : 'rgba(45, 96, 192, 1)'
            }
            const gradient = ctx.createLinearGradient(
              chartArea.left,
              chartArea.top,
              chartArea.left,
              chartArea.bottom,
            )
            if (context.dataIndex === 0) {
              gradient.addColorStop(0, 'rgba(34, 197, 94, 0.2)')
              gradient.addColorStop(1, 'rgba(34, 197, 94, 1)')
            } else {
              gradient.addColorStop(0, 'rgba(45, 67, 192, 0.2)')
              gradient.addColorStop(1, 'rgba(45, 96, 192, 1)')
            }
            return gradient
          },
        },
      ],
    }),
    [affirmativeVotes, negativeVotes, hall],
  )

  const options = useMemo(
    () => ({
      plugins: {
        legend: {
          labels: {
            color: isDark ? '#fff' : '#000',
            generateLabels: (chart) => {
              const labels = chart.config.data.labels
              const meta = chart.getDatasetMeta(0)
              if (labels && labels.length) {
                return labels.map((label, index) => {
                  const solidColor = index === 0 ? 'rgba(34, 197, 94, 1)' : 'rgba(45, 96, 192, 1)'
                  return {
                    text: label,
                    fillStyle: solidColor,
                    strokeStyle: solidColor,
                    hidden: meta.data[index].hidden,
                    index: index,
                    lineCap: 'butt',
                    lineDash: [],
                    lineDashOffset: 0,
                    lineJoin: 'miter',
                    pointStyle: 'circle',
                    fontColor: isDark ? '#fff' : '#000',
                  }
                })
              }
              return []
            },
          },
          onClick: (e, legendItem, legend) => {
            const index = legendItem.index
            const ci = legend.chart
            const meta = ci.getDatasetMeta(0)
            meta.data[index].hidden = !meta.data[index].hidden
            ci.update()
          },
        },
        tooltip: {
          callbacks: {
            label: function (context) {
              const label = context.label || ''
              let users: HallUser[] = []
              if (context.dataIndex === 0) {
                users = simpleResults[SpeakingRole.Affirmative]
              } else if (context.dataIndex === 1) {
                users = simpleResults[SpeakingRole.Negative]
              }
              const userNames = users.map((user) => user?.displayName).filter(Boolean)
              return `${label}: ${userNames.join(', ')}`
            },
          },
        },
      },
    }),
    [simpleResults, hall],
  )

  return (
    <div className="flex flex-col items-center w-full h-full">
      <DialogTitle className="text-2xl font-bold mb-6 text-center">
        Audience Poll Results
      </DialogTitle>

      {pollResults.castVotes === 0 ? (
        <div className="flex items-center justify-center text-center text-muted-foreground h-full min-h-[400px] px-6 font-semibold">
          <TextLoading text="Waiting for audience members to cast their votes" />
        </div>
      ) : (
        <div className="w-full max-w-md">
          <Pie data={data} options={options} />
        </div>
      )}
    </div>
  )
}
